import { v4 as uuidv4 } from 'uuid'
import {
    CreateCartItemSelfcareCommand,
    useCreateSelfcareCartItemMutation,
    useDeleteSelfcareCartItemsByIdMutation,
} from 'store/api-slice/hooks'
import { useApp } from 'store/steps'
import { useSelfcareCart } from 'store/selfcare-cart-slice/hooks'
import { PromiseResponse } from 'helpers/types'
import { ErrorField, handleError } from 'helpers/errors'
import { useContext } from 'react'
import { GuestsDataContext } from './guestsDataProvider'
import { useAppSelector } from 'store/hooks'
import { MarchVoucher } from 'store/api-slice/types'

export const useGuestsSelfcareCart = (): {
    cartItems: MarchVoucher[]
    addItemsToCart: () => Promise<PromiseResponse>
    deleteItemsFromCart: () => Promise<PromiseResponse>
    isSubmitLoading: boolean
    isDeleteLoading: boolean
} => {
    const { language } = useApp()
    const { refetchSelfcareCart } = useSelfcareCart()

    const { marchVouchersData, setMarchVouchersData } =
        useContext(GuestsDataContext)

    const { marchVouchers } = useAppSelector(
        (state) => state.selfcareCart.items
    )

    const [postSelfcareCartItem, postSelfcareStatus] =
        useCreateSelfcareCartItemMutation()
    const { isLoading: isSubmitLoading } = postSelfcareStatus

    const [deleteSelfcareCartItems, deleteSelfcareItemsStatus] =
        useDeleteSelfcareCartItemsByIdMutation()
    const { isLoading: isDeleteLoading } = deleteSelfcareItemsStatus

    const validateInputValues = () => {
        // TODO: figure out some better solution
        const validatedData = marchVouchersData.map((data) => {
            const { firstName, lastName } = data
            const isFirstNameError =
                !firstName || !/^([^0-9]{2,})$/.test(firstName)
            const isLastNameError =
                !lastName || !/^([^0-9]{2,})$/.test(lastName)

            return {
                ...data,
                isFirstNameError,
                isLastNameError,
            }
        })
        const hasErrors = validatedData.some(
            ({ isFirstNameError, isLastNameError }) =>
                isFirstNameError || isLastNameError
        )

        setMarchVouchersData(validatedData)
        return !hasErrors
    }

    const addItemsToCart = async () => {
        if (!validateInputValues()) return { isSuccess: false }

        const cartItemCommandData = marchVouchersData
            .map((data) => {
                const { lastName, firstName, productId, tickets } = data

                const cartItemDataByTickets: CreateCartItemSelfcareCommand[] =
                    tickets.map(({ ticketId }) => ({
                        cartItemId: uuidv4(),
                        productId,
                        productType: 'voucher',
                        quantity: 1,
                        detail: {
                            voucherType: 'next-visit',
                            cartItemId: ticketId,
                            name: firstName?.trim(),
                            surname: lastName?.trim(),
                            type: 'voucher',
                        },
                    }))

                return cartItemDataByTickets
            })
            .flat()

        const postActions = cartItemCommandData.map((cartItemData) =>
            postSelfcareCartItem({
                createCartItemSelfcareCommand: {
                    ...cartItemData,
                },
                _locale: language,
            })
        )

        try {
            // TODO: figure out better solution
            // eslint-disable-next-line compat/compat
            const response = await Promise.allSettled(postActions)
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            //@ts-ignore
            const isError = response.some(({ status, value }) => !!value?.error)
            !isError && refetchSelfcareCart()
            return { isSuccess: !isError }
        } catch (errors: unknown) {
            const { messages } = errors as {
                messages: ErrorField[]
            }
            return { isSuccess: false, messages }
        }
    }

    const deleteItemsFromCart = async () => {
        const vouchersIdsToRemove = marchVouchers.map(
            (voucher) => voucher.cartItemId || ''
        )

        if (!vouchersIdsToRemove.length) {
            return { isSuccess: true }
        }

        try {
            const cartItemCommandData = {
                cartItemUuids: vouchersIdsToRemove,
            }
            const response = await deleteSelfcareCartItems({
                _locale: language,
                deleteCartItemsByIdSelfcareCommand: {
                    ...cartItemCommandData,
                },
            })

            handleError(response)
            refetchSelfcareCart()
            return { isSuccess: true }
        } catch (errors: unknown) {
            const { messages } = errors as {
                messages: ErrorField[]
            }
            return { isSuccess: false, messages }
        }
    }

    return {
        cartItems: marchVouchers,
        addItemsToCart,
        deleteItemsFromCart,
        isSubmitLoading,
        isDeleteLoading,
    }
}
